import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { displayPhone } from '../../lib/phone';

// Inline upload + confirmation flow (mirrors the AddOwnerForm / AttachPicker
// pattern). Pick a PDF → POST /records/extract → confirm the horse / owner /
// provider matches → save the health record. The PDF is stored on upload; if
// extraction fails the record can still be filled in and saved manually.

const TYPE_OPTIONS = [
  ['vet', 'Vet'],
  ['coggins', 'Coggins'],
  ['vaccination', 'Vaccination'],
  ['dental', 'Dental'],
  ['farrier', 'Farrier'],
  ['injection', 'Injection'],
  ['injury', 'Injury'],
  ['deworming', 'Deworming'],
  ['other', 'Other'],
];

function todayISO() {
  const d = new Date();
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
}

export default function VetRecordUpload({ horseId, horse, owners = [], onDone, onCancel }) {
  const inputRef = useRef(null);
  const [draft, setDraft] = useState(null);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  const [recordType, setRecordType] = useState('vet');
  const [date, setDate] = useState(todayISO());
  const [title, setTitle] = useState('');
  const [details, setDetails] = useState('');
  const [providerChoice, setProviderChoice] = useState('none'); // 'none' | 'new' | <providerId>

  const extract = useMutation({
    mutationFn: (file) => api.upload(`/horses/${horseId}/records/extract`, file),
    onSuccess: (data) => {
      setDraft(data);
      const ex = data.extracted || {};
      setDate(ex.document_date || todayISO());
      if (data.suggested?.providerId) setProviderChoice(data.suggested.providerId);
      else if (ex.provider && (ex.provider.name || ex.provider.business_name)) setProviderChoice('new');
      else setProviderChoice('none');
      setErr('');
    },
    onError: (e) => setErr(friendly(e)),
  });

  function onPick(e) {
    const file = (e.target.files || [])[0];
    e.target.value = '';
    if (!file) return;
    if (file.type !== 'application/pdf') return setErr('Please choose a PDF.');
    if (file.size > 20 * 1024 * 1024) return setErr('PDF is over 20 MB.');
    setErr('');
    extract.mutate(file);
  }

  async function save() {
    setErr('');
    setSaving(true);
    try {
      let providerId = null;
      const ex = draft.extracted || {};

      if (providerChoice === 'new') {
        const p = ex.provider || {};
        const name = (p.name || p.business_name || '').trim();
        if (!name) throw new Error('provider_name_missing');
        const created = await api.post('/providers', {
          name,
          business_name: p.business_name || undefined,
          email: p.email || undefined,
          phones: p.phone ? [{ label: 'Office', number: p.phone }] : [],
        });
        providerId = created.id;
        await api.post(`/providers/${providerId}/horses/${horseId}`, { relationship: 'Vet' });
      } else if (providerChoice !== 'none') {
        providerId = providerChoice;
      }

      await api.post(`/horses/${horseId}/records`, {
        document: draft.document,
        recordType,
        date,
        title: title.trim() || undefined,
        details: details.trim() || undefined,
        providerId: providerId || undefined,
        extracted: draft.extracted || undefined,
      });
      onDone();
    } catch (e) {
      setErr(friendly(e));
      setSaving(false);
    }
  }

  // ---- pick step ----
  if (!draft) {
    return (
      <div className="mb-3 pb-3 border-b border-ink-100">
        <input ref={inputRef} type="file" accept="application/pdf" className="hidden" onChange={onPick} />
        <div className="flex items-center gap-3">
          <button
            onClick={() => inputRef.current?.click()}
            disabled={extract.isPending}
            className="btn-primary text-sm"
          >
            {extract.isPending ? 'Reading document…' : 'Choose PDF'}
          </button>
          <button onClick={onCancel} className="text-sm text-ink-500 hover:text-ink-800">
            Cancel
          </button>
        </div>
        {extract.isPending && (
          <p className="mt-2 text-sm text-ink-400">Uploading and extracting details…</p>
        )}
        {err && <p className="mt-2 text-sm text-red-600">{err}</p>}
      </div>
    );
  }

  // ---- confirm step ----
  const ex = draft.extracted || {};
  const exHorse = ex.horse || {};
  const exProvider = ex.provider || {};
  const horseMatch = !!draft.suggested?.horseNameMatches;
  const ownerMatch = draft.suggested?.ownerUserId
    ? owners.find((o) => o.userId === draft.suggested.ownerUserId)
    : null;
  const hasExtractedProvider = !!(exProvider.name || exProvider.business_name);

  return (
    <div className="mb-3 pb-3 border-b border-ink-100 space-y-4">
      {draft.extractError && (
        <p className="text-sm text-amber-600">
          Couldn’t auto-read the document — confirm the matches below and fill in anything missing.
        </p>
      )}

      <Check
        ok={horseMatch}
        label="Horse"
        good={`Document is for “${exHorse.name || '—'}” — matches ${horse?.name}.`}
        warn={
          exHorse.name
            ? `Document says “${exHorse.name}”, but this horse is “${horse?.name}.” Confirm it's the right horse.`
            : `Couldn’t read a horse name — confirm this PDF belongs to ${horse?.name}.`
        }
        sub={corroborate(exHorse, horse)}
      />

      <Check
        ok={!!ownerMatch}
        label="Owner"
        good={`Client “${ex.owner?.name || '—'}” matches owner ${ownerMatch?.name || ''}.`}
        warn={
          ex.owner?.name
            ? `Client is “${ex.owner.name}”, which doesn’t match an owner on file (${ownerList(owners)}).`
            : `Couldn’t read a client name. Owners on file: ${ownerList(owners)}.`
        }
      />

      <div>
        <label className="block text-sm font-medium text-ink-700 mb-1">Provider</label>
        {hasExtractedProvider && (
          <p className="text-xs text-ink-500 mb-1">
            From document: {exProvider.name || exProvider.business_name}
            {exProvider.name && exProvider.business_name ? ` · ${exProvider.business_name}` : ''}
            {exProvider.phone ? ` · ${displayPhone(exProvider.phone)}` : ''}
            {exProvider.email ? ` · ${exProvider.email}` : ''}
          </p>
        )}
        <select className="input" value={providerChoice} onChange={(e) => setProviderChoice(e.target.value)}>
          {hasExtractedProvider && (
            <option value="new">+ Add “{exProvider.name || exProvider.business_name}” as a provider</option>
          )}
          {(draft.providers || []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
              {p.businessName ? ` — ${p.businessName}` : ''}
            </option>
          ))}
          <option value="none">No provider</option>
        </select>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-ink-700 mb-1">Type</label>
          <select className="input" value={recordType} onChange={(e) => setRecordType(e.target.value)}>
            {TYPE_OPTIONS.map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-ink-700 mb-1">Date</label>
          <input type="date" className="input" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-ink-700 mb-1">Title (optional)</label>
        <input
          className="input"
          placeholder="e.g. Clinical history"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-ink-700 mb-1">Notes (optional)</label>
        <textarea className="input" rows={2} value={details} onChange={(e) => setDetails(e.target.value)} />
      </div>

      <p className="text-xs text-ink-400">Attached PDF: {draft.document?.name}</p>

      {err && <p className="text-sm text-red-600">{err}</p>}

      <div className="flex gap-2">
        <button onClick={save} disabled={saving} className="btn-primary">
          {saving ? 'Saving…' : horseMatch && ownerMatch ? 'Save Record' : 'Save Anyway'}
        </button>
        <button
          onClick={onCancel}
          disabled={saving}
          className="text-sm text-ink-500 hover:text-ink-800 px-2"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function Check({ ok, label, good, warn, sub }) {
  return (
    <div className={`rounded-md p-2 text-sm ${ok ? 'bg-emerald-50' : 'bg-amber-50'}`}>
      <div className="flex items-start gap-2">
        <span className={ok ? 'text-emerald-600' : 'text-amber-600'}>{ok ? '✓' : '⚠'}</span>
        <div className="min-w-0">
          <span className="font-medium text-ink-700">{label}: </span>
          <span className="text-ink-700">{ok ? good : warn}</span>
          {sub && <div className="text-xs text-ink-500 mt-0.5">{sub}</div>}
        </div>
      </div>
    </div>
  );
}

function ownerList(owners) {
  return owners.map((o) => o.name).filter(Boolean).join(', ') || 'none';
}

function corroborate(exHorse, horse) {
  const bits = [];
  if (exHorse.breed || horse?.breed) bits.push(`breed ${exHorse.breed || '—'} / ${horse?.breed || '—'}`);
  if (exHorse.sex || horse?.sex) bits.push(`sex ${exHorse.sex || '—'} / ${horse?.sex || '—'}`);
  if (exHorse.color || horse?.color) bits.push(`color ${exHorse.color || '—'} / ${horse?.color || '—'}`);
  if (exHorse.birthdate || horse?.birthdate) bits.push(`DOB ${exHorse.birthdate || '—'} / ${horse?.birthdate || '—'}`);
  return bits.length ? `document / on file — ${bits.join(', ')}` : null;
}

function friendly(e) {
  const code = e?.body?.error || e?.message || '';
  switch (code) {
    case 'missing_file':
      return 'No file received. Try again.';
    case 'unsupported_type':
      return 'That file type isn’t supported — upload a PDF.';
    case 'file_too_large':
      return 'The PDF is too large (20 MB max).';
    case 'horse_not_found':
      return 'This horse wasn’t found in your barn.';
    case 'provider_name_missing':
      return 'Couldn’t read a provider name to add — pick an existing one or “No provider.”';
    case 'missing_date':
      return 'Pick a date for the record.';
    case 'forbidden':
      return 'Only an owner or manager can add records.';
    default:
      return 'Something went wrong. Try again.';
  }
}
